import { TrashIcon } from "@heroicons/react/24/outline";
import React, { useEffect } from "react";
import { useReportDelete } from "../api/ReportsApi";
import {
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../shared-components/ConfirmationDialog";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { useAppContext } from "../AppProviders";
import { CacheKeys } from "../api/Cache";
import { useFamaFlags } from "../api/FamaFlags";

type Props = {
  reportUUID: string;
  /** When LD flag is false, called after delete so the parent can refetch the list. */
  onDeletedSuccess?: () => void;
};

const ReportDeleteButton = ({ reportUUID, onDeletedSuccess }: Props) => {
  const { refreshCache } = useAppContext();
  const { v3UiReactsToWebsocketWorkflowEvents: workflowEventsEnabled } =
    useFamaFlags();
  const {
    mutate: reportDelete,
    error: errorReports,
    reset,
    isSuccess,
    isLoading,
  } = useReportDelete();
  const { modal, show, hide } = useConfirmationDialog();

  function handleDelete(e: React.MouseEvent<SVGSVGElement, MouseEvent>) {
    e.preventDefault();

    if (!reportUUID) {
      show(
        "Wrong Report UUID",
        "",
        () => {},
        <>The report is in an invalid state</>,
        false
      );
    }

    show(
      "Report Removal Confirmation",
      "Are you sure you want to remove the report?",
      (action: ConfirmDialogActions) => {
        if (action === "close") {
          hide();
          return;
        }

        reportDelete(reportUUID);
      }
    );
  }

  useEffect(() => {
    if (errorReports) {
      show(
        "Error Removing the report:",
        "",
        () => {},
        <div className="my-2">
          <ErrorMessageContainer>
            <>
              <strong className="block py-1">{reportUUID}</strong>
              Please try again or contact the administrator.
            </>
          </ErrorMessageContainer>
        </div>,
        false
      );
      reset();
    }
  }, [errorReports, reportUUID, reset, show]);

  useEffect(() => {
    if (isLoading) {
      show("", "", () => {}, <LoadingSpinner />, false);
    }
    if (isSuccess) {
      if (!workflowEventsEnabled) {
        refreshCache(CacheKeys.reports);
        onDeletedSuccess?.();
      }
      hide();
      reset();
    }
  }, [
    show,
    isSuccess,
    isLoading,
    workflowEventsEnabled,
    refreshCache,
    onDeletedSuccess,
    hide,
    reset,
  ]);

  return (
    <>
      <TrashIcon
        className="w-6 text-gray-400 cursor-pointer report-delete-button"
        onClick={handleDelete}
      />
      {modal}
    </>
  );
};

export default ReportDeleteButton;
